'use client'
import { gtmFromRedirectedToCompany } from '@/app/[lng]/components/GTMTrackers/GTMCompanyTracker'
import { useGTMSalesOfferTracker } from '@/app/[lng]/components/GTMTrackers/GTMSalesOfferTracker'
import PlanLogo from '@/app/[lng]/components/PlanLogo'
import DetailsPageSkeleton from '@/app/[lng]/components/Skeletons/DetailsPageSkeleton'
import { AppDispatch, RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import { CountryType, ListType } from '@/app/types/constantDataType'
import { OffersType } from '@/app/types/offersType'
import { AvailableMonthList } from '@/components/AvailableMonthList'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import ImagePreview from '@/components/ui/imagePreview'
import { Separator } from '@/components/ui/separator'
import ShareSocials from '@/components/ui/ShareSocials'
import { companyProfileRedirection } from '@/lib/redirectionUtils'
import { getCountryList } from '@/services/generalDataServices'
import { getOfferDetails } from '@/services/my_fructidor/OffersServices'
import { Avatar, Popover } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import OfferContactModal from '../(modal)/OfferContactModal'
import AlternateOffers from './AlternateOffers'
import HotOffers from './HotOffers'

interface OfferCardProps {
  Offer_id: number
}
export interface PickupPoint {
  pickup_city: ListType
  pickup_country: ListType
}

const SalesOfferDetail: React.FC<OfferCardProps> = ({ Offer_id }) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const params = useParams()
  const countryList = useSelector(
    (state: RootState) => state.constantData.countriesList
  )
  const { offer, isOfferLoading } = useSelector(
    (state: RootState) => state.offerData
  )
  useGTMSalesOfferTracker(Number(offer?.offer?.company?.id))
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const id = open ? 'sales-offer-pickup-point' : undefined
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const findEmoji = (countryList: CountryType[], countryName: string) => {
    return countryList.find((item) => item.name === countryName)?.emoji
  }

  useEffect(() => {
    if (countryList.length <= 0) {
      dispatch(getCountryList())
    }
  }, [])
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getOfferDetails(Offer_id))
      } catch (error) {
        console.error('Error fetching offer details:', error)
      }
    }
    fetchData()
  }, [Offer_id, dispatch])

  const title = ` ${offer?.offer?.product?.name}
  ${offer?.offer?.varieties?.length > 0 ? `- ${offer?.offer?.varieties[0]?.name}` : ''}
  ${offer?.offer?.origins?.[0]?.name ? ` - ${t('Origin')} ${offer?.offer?.origins?.[0]?.name}` : ''}`
  return (
    <div className="basis-4/5">
      <Breadcrumb className="pb-7 mobile:pb-3">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/sales_offers">{t('Home')}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{t('Sales Offer Details')}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      {!isOfferLoading && offer ? (
        <div className="flex flex-row w-full gap-5 mobile:relative">
          <ImagePreview
            images={offer?.offer?.images ?? []}
            title={title}
            cardType={'offer'}
          />
          <div className="flex flex-col ">
            <>
              <div className="flex justify-between items-start relative pr-1">
                <div className="w-[90%]">
                  <p className="text-xs text-light_black">
                    <span className="mr-[5px]">{t('Creation Date')}:</span>
                    {offer?.offer?.created_at
                      ? offer?.offer?.created_at
                      : t('Data not present')}
                  </p>
                  <h2 className="font-ns_bold text-lg flex gap-3">{title}</h2>
                </div>
                <div className="mobile:absolute mobile:right-0">
                  <ShareSocials
                    sharAbleLink={`${window.location.origin}/${params?.lng}/sales_offers/${Offer_id}`}
                    shareText={`${offer?.offer?.product?.name} ${offer?.offer?.origins?.[0]?.name ? `${t('Origin')} ${offer?.offer?.origins?.[0]?.name}` : ''} `}
                    shareBg={true}
                  />
                </div>
              </div>

              <div className="py-2 mb-3">
                {offer?.offer?.product_type?.length > 0 ? (
                  <span className="text-sm border bg-chip_bg border-chip_border rounded-full  px-2">
                    {t(
                      offer?.offer?.product_type
                        ?.map((item: ListType) => item?.name)
                        ?.join(' & ')
                    )}
                  </span>
                ) : (
                  ''
                )}
              </div>
            </>
            <div className="mobile:flex flex-col hidden">
              <div className="h-[130px] w-full relative">
                <div className="h-[130px] w-full bg-[#ededed] overflow-hidden rounded-sm rounded-tr-sm rounded-br-none">
                  <Image
                    loading="lazy"
                    width={409}
                    height={306}
                    src={
                      offer?.offer?.images?.length > 0
                        ? offer?.offer?.images[0]?.url
                        : '/assets/Icons/noImage/noImage2.svg'
                    }
                    alt="offer-img"
                    className={`h-[306px] w-[409px] mobile:w-full mobile:h-[130px] ${
                      offer?.offer?.images?.length > 0
                        ? 'w-full h-full object-cover'
                        : 'w-full h-full object-contain'
                    } `}
                  />
                </div>
                <span className="text-sm bg-secondary w-fit rounded-l-sm text-white absolute p-1 px-3 right-0 bottom-0">
                  {t('OFFER')}
                </span>
              </div>
              {offer?.offer?.images?.length > 1 && (
                <div className="flex h-20 gap-2 mt-3 flex-warp">
                  {offer?.offer?.images
                    ?.slice(1, offer?.offer?.images?.length)
                    .map((item: any, index: number) => (
                      <Image
                        key={index}
                        width={80}
                        height={75}
                        className="w-[80px] h-[75px]"
                        src={item?.url ?? '/assets/Icons/noImage/noImage2.svg'}
                        alt=""
                      />
                    ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3 font-ns_bold text-sm mobile:gap-2 mobile:mt-2">
              <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                <p className="text-base text-[16px] font-ns_bold mobile:flex">
                  {t('Origin')}
                  <span className="hidden mobile:block mobile:mx-1">:</span>
                </p>
                <span className="font-light">
                  {offer?.offer?.origins?.length == 0 && t('NA')}
                  {offer?.offer?.origins?.length > 0 && (
                    <span className=" flex flex-row">
                      {offer?.offer?.origins
                        ?.map((item: any) => item.name)
                        ?.join(', ')}
                    </span>
                  )}
                </span>
              </div>
              {offer?.offer?.varieties?.length > 0 && (
                <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                  <p className="text-base font-ns_bold mobile:flex">
                    {t('Variety')}{' '}
                    <span className="hidden mobile:block mobile:mx-1">:</span>
                  </p>
                  <div className="flex flex-wrap w-full gap-x-1 ml-1">
                    {offer?.offer?.varieties?.map(
                      (
                        variety: { id: number; name: string },
                        index: number
                      ) => (
                        <span
                          key={variety.id + 'variety_id'}
                          className="whitespace-nowrap"
                        >
                          {variety.name}
                          {index < offer.offer.varieties.length - 1 && ','}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col mobile:flex-col-reverse gap-3 mobile:gap-2">
                <div>
                  <p className="text-base font-ns_bold">{t('Availability')} </p>
                  <div className="flex flex-row text-primary-foreground w-full gap-1.5">
                    <AvailableMonthList
                      availableMonths={offer?.offer?.available_months?.months}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-base mobile:flex-row mobile:items-baseline">
                  <p className="font-ns_bold text-base mobile:flex">
                    {t('Price')}{' '}
                    <span className="hidden mobile:block mobile:mx-1">:</span>
                  </p>
                  <p className="text-base font-bold">
                    <span className="text-orange">
                      {offer?.offer?.price_unit?.code
                        ? offer?.offer?.price_unit?.code?.toUpperCase()
                        : ''}{' '}
                    </span>
                    {offer?.offer?.price && offer?.offer?.price !== 0.0 ? (
                      <span className="text-orange">{offer?.offer?.price}</span>
                    ) : (
                      <span className="text-black font-light text-sm">
                        {t('NA')}
                      </span>
                    )}
                    {offer.offer?.price_per?.name ? (
                      <span className="text-orange">
                        {' '}
                        / {offer?.offer?.price_per?.name}
                      </span>
                    ) : (
                      ''
                    )}
                  </p>
                </div>
                <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                  <p className="text-base font-ns_bold mobile:flex">
                    {t('Contact Preference')}
                    <span className="hidden mobile:block mobile:mx-1">:</span>
                  </p>
                  <p className="font-light">
                    {(offer?.contact as OffersType)?.contact_preference
                      ? offer?.contact?.contact_preference === 'both'
                        ? t('Email or Phone')
                        : offer?.contact?.contact_preference
                      : t('NA')}
                  </p>
                </div>
              </div>
              <div className="shadow-[0_0_4px_0_rgba(0,0,0,0.16)] p-4 py-3 rounded flex gap-4 mt-2">
                <Image
                  loading="lazy"
                  width={45}
                  height={45}
                  src="/assets/Icons/fruitBasket.svg"
                  alt=""
                />
                <div className="flex flex-col gap-1">
                  <p>
                    <span className="font-ns_bold">{t('Caliber')} : </span>
                    <span className="font-light">
                      {offer?.offer.size ? offer?.offer.size : t('NA')}
                    </span>
                  </p>
                  <p>
                    <span className="font-ns_bold">{t('Quantity')} : </span>
                    <span className="font-light">
                      {offer?.offer?.quantity ? (
                        <>
                          <span>{offer?.offer?.quantity}</span>{' '}
                          <span>
                            {offer?.offer?.quantity_unit
                              ? offer?.offer?.quantity_unit?.name
                              : ''}
                          </span>
                        </>
                      ) : (
                        t('NA')
                      )}
                    </span>
                  </p>
                  <p className=" flex flex-row">
                    <span className="font-ns_bold">{t('Quality')} : </span>
                    <span className="font-light">
                      {offer?.offer?.quality?.length > 0 ? (
                        <div className="flex flex-wrap w-full gap-x-1 ml-1">
                          {offer?.offer?.quality?.map(
                            (
                              quality: { id: number; name: string },
                              index: number
                            ) => (
                              <span
                                key={quality.id}
                                className="whitespace-nowrap"
                              >
                                {quality.name}
                                {index < offer.offer.quality.length - 1 && ','}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        t('NA')
                      )}
                    </span>
                  </p>
                </div>
              </div>
              <div className="shadow-[0_0_4px_0_rgba(0,0,0,0.16)] p-4 py-2 flex gap-4">
                <span>
                  <Image
                    loading="lazy"
                    width={45}
                    height={45}
                    src="/assets/Icons/truck.svg"
                    alt=""
                  />
                </span>
                <div className="flex flex-col gap-1">
                  <p>
                    <span className="font-ns_bold">{t('Packaging')} : </span>
                    <span className="font-light">
                      {offer?.offer?.packaging
                        ? offer?.offer?.packaging
                        : t('NA')}
                    </span>
                  </p>
                  <p>
                    <span className="font-ns_bold">
                      {t('Minimum Order')} :{' '}
                    </span>
                    <span className="font-light">
                      {offer?.offer?.min_order > 0 ? (
                        <>
                          <span>{offer?.offer?.min_order}</span>{' '}
                          <span>
                            {offer?.offer?.min_order_unit?.name
                              ? offer?.offer?.min_order_unit?.name
                              : ''}
                          </span>
                        </>
                      ) : (
                        t('NA')
                      )}
                    </span>
                  </p>
                  <span className="">
                    <span className="font-ns_bold items-center">
                      {t('Pick-up Point')} :
                    </span>
                    <span className="font-light items-center">
                      {offer?.offer?.pickup_points?.length == 0 && t('NA')}

                      {offer?.offer?.pickup_points?.length >= 1 && (
                        <span className=" flex">
                          <span className=" flex flex-row ml-1 items-center">
                            {
                              offer?.offer?.pickup_points[0]?.pickup_country
                                .name
                            }
                            {offer?.offer?.pickup_points[0]?.pickup_city
                              ?.name && (
                              <>
                                <span className=" ml-1 mr-1">-</span>
                                {
                                  offer?.offer?.pickup_points[0]?.pickup_city
                                    ?.name
                                }
                              </>
                            )}
                          </span>
                          {offer?.offer?.pickup_points?.length > 1 && (
                            <span className=" flex flex-row ml-2">
                              <Avatar
                                className="  !h-[20px] !w-[20px] !text-[10px]"
                                aria-describedby={id}
                                onMouseOver={(event) => {
                                  handleClick(event)
                                }}
                              >{`  +${offer?.offer?.pickup_points?.length - 1}`}</Avatar>
                            </span>
                          )}

                          <Popover
                            className=" ml-2"
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                              vertical: 'center',
                              horizontal: 'right',
                            }}
                          >
                            <div className=" p-4 gap-1 ">
                              {offer?.offer?.pickup_points
                                ?.slice(1, offer?.offer?.pickup_points?.length)
                                .map(
                                  (
                                    pickPointItem: PickupPoint,
                                    index: number
                                  ) => (
                                    <div
                                      key={
                                        index +
                                        pickPointItem?.pickup_country?.id
                                      }
                                      className=" flex flex-row gap-2"
                                      style={{
                                        backgroundColor: 'white',
                                        boxShadow: 'none',
                                        borderRadius: 0,
                                      }}
                                    >
                                      <Avatar className=" !w-[20px] !h-[20px]">
                                        {findEmoji(
                                          countryList,
                                          pickPointItem?.pickup_country?.name
                                        )}
                                      </Avatar>
                                      <span>
                                        {pickPointItem?.pickup_country?.name}
                                      </span>
                                      {pickPointItem?.pickup_city?.name && (
                                        <>
                                          <span>-</span>
                                          <span>
                                            {pickPointItem?.pickup_city?.name}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  )
                                )}
                            </div>
                          </Popover>
                        </span>
                      )}
                    </span>
                  </span>
                </div>
              </div>
              <div className="font-ns_bold mt-2 flex gap-3 ">
                <Link
                  className="font-bold"
                  href={companyProfileRedirection(
                    offer?.offer?.company?.industry?.name ?? '',
                    offer?.offer?.company?.friendly_url ?? '',
                    String(params?.lng) ?? 'en'
                  )}
                  onClick={() => {
                    gtmFromRedirectedToCompany(
                      'sales_offer_detail',
                      offer?.offer?.company?.friendly_url,
                      offer?.offer?.company?.id
                    )
                  }}
                >
                  {offer?.offer?.company?.name}
                </Link>
                - {offer?.offer?.origins?.[0]?.name ?? ''}
                <PlanLogo planType={offer?.offer?.company?.subscription} />
              </div>
              <div className="py-2">
                <OfferContactModal />
              </div>
              <div className="flex flex-col gap-y-1 text-[16px] font-bold mobile:flex-row">
                <p className="mb-[10px] mobile:mb-0 mobile:mr-1 mobile:flex">
                  {t('Additional Information')}
                  <span className="mobile:block hidden mobile:mx-1">:</span>
                </p>
                {offer?.offer?.comment ? (
                  <p className="font-light">{offer?.offer?.comment}</p>
                ) : (
                  <span className="font-light">{t('NA')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <DetailsPageSkeleton />
      )}
      <Separator className="mt-5 w-[954px] mobile-width" />
      <AlternateOffers offerId={Offer_id} />
      <HotOffers offerId={Offer_id} />
    </div>
  )
}

export default SalesOfferDetail
